import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from '@mc/i18n'
import { GlobalErrorModal } from './GlobalErrorModal'

type GlobalErrorModalOptions = {
  title?: string
  description?: string
  primaryActionLabel?: string
  onPrimaryAction?: () => void
}

interface GlobalErrorModalContextValue {
  openModal: (options?: GlobalErrorModalOptions) => void
  closeModal: () => void
}

const GlobalErrorModalContext = createContext<
  GlobalErrorModalContextValue | undefined
>(undefined)

const reloadPage = () => {
  if (typeof window !== 'undefined') {
    window.location.reload()
  }
}

export const GlobalErrorModalProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { t } = useTranslation('@mc/educator')
  const [modalState, setModalState] = useState<
    GlobalErrorModalOptions & { isOpen: boolean }
  >({
    isOpen: false,
  })

  const openModal = useCallback(
    (options?: GlobalErrorModalOptions) => {
      setModalState({
        isOpen: true,
        title: options?.title ?? t('globalError.title'),
        description: options?.description ?? t('globalError.description'),
        primaryActionLabel:
          options?.primaryActionLabel ?? t('globalError.reloadCta'),
        onPrimaryAction: options?.onPrimaryAction ?? reloadPage,
      })
    },
    [t],
  )

  const closeModal = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }))
  }, [])

  const contextValue = useMemo(
    () => ({
      openModal,
      closeModal,
    }),
    [openModal, closeModal],
  )

  return (
    <GlobalErrorModalContext.Provider value={contextValue}>
      {children}
      <GlobalErrorModal
        isOpen={modalState.isOpen}
        title={modalState.title ?? t('globalError.title')}
        description={modalState.description ?? t('globalError.description')}
        primaryActionLabel={
          modalState.primaryActionLabel ?? t('globalError.reloadCta')
        }
        onPrimaryAction={() => {
          modalState.onPrimaryAction?.()
          closeModal()
        }}
      />
    </GlobalErrorModalContext.Provider>
  )
}

export const useGlobalErrorModal = () => {
  const context = useContext(GlobalErrorModalContext)

  if (!context) {
    throw new Error(
      'useGlobalErrorModal must be used within a GlobalErrorModalProvider',
    )
  }

  return context
}
